import type { NavigateFunction } from 'react-router-dom';
import { checkUserConsent, getBuildingDetails } from '../../auth/_requests';
import type { buildingDetails, ModalState } from './types';

export function isBuildingPayInfoComplete(building: buildingDetails | undefined): boolean {
  if (!building) return false;
  const hasRnt = !!building.building.payNumber;
  const hasManager = !!building.tenantManager;
  return hasManager || hasRnt;
}

export function hasUsableBuildingAddress(
  address: buildingDetails['address'] | null | undefined
): address is buildingDetails['address'] {
  if (!address) return false;
  if (address.fullAddress?.trim()) return true;
  return Boolean(
    address.streetName?.trim() && address.city?.trim() && address.postalCode?.trim()
  );
}

/** Matches dashboard Properties pay gate: landlord or rent reference on the selected home. */
export function canContinuePayRentAfterPropertyStep(
  building: buildingDetails | undefined
): boolean {
  if (!building) return false;
  return isBuildingPayInfoComplete(building);
}

/** Show dashboard IDV banner until the tenant completes identity verification. */
export function shouldShowDashboardIdvAuthHold(isIdentityVerified: boolean): boolean {
  return !isIdentityVerified;
}

/**
 * Opens building prerequisite modals (complete-building) when needed.
 * IDV is deferred until after the user's first payment (auth hold flow).
 * Returns true when the user may continue (selected building complete).
 */
export async function ensurePayPrerequisitesMet(
  tenantBuildingId: string,
  buildings: buildingDetails[] | undefined | null,
  setModal: (modal: string | null, state?: ModalState) => void
): Promise<boolean> {
  const buildingItem = buildings?.find(
    (building) => building.building.tenantBuildingId === tenantBuildingId
  );

  if (!isBuildingPayInfoComplete(buildingItem)) {
    setModal('complete-building', { tenantBuildingId });
    return false;
  }

  return true;
}

export async function continuePayRentSetupAfterTerms(
  token: string,
  setModal: (modal: string | null, state?: ModalState) => void,
  navigate: NavigateFunction
): Promise<void> {
  try {
    const buildingsRes = await getBuildingDetails(token);
    const buildings = buildingsRes.data;
    const tenantBuildingId = buildings?.[0]?.building.tenantBuildingId;

    if (!tenantBuildingId) {
      setModal('complete-building', { payRentSetupFlow: true });
      return;
    }

    const prerequisitesMet = await ensurePayPrerequisitesMet(tenantBuildingId, buildings, setModal);
    if (!prerequisitesMet) return;

    navigate('/pay-rent', { state: { buildingId: tenantBuildingId } });
  } catch {
    setModal('complete-building', { payRentSetupFlow: true });
  }
}

export async function openDashboardPayRentFlow(
  buildings: buildingDetails[] | undefined | null,
  setModal: (modal: string | null, state?: ModalState) => void,
  navigate: NavigateFunction,
  token?: string,
  modalState?: ModalState | null
): Promise<void> {
  if (token) {
    try {
      const consentResponse = await checkUserConsent(token);
      if (consentResponse?.data?.isUserConsentRequired) {
        setModal('terms-services', {
          ...(modalState ?? {}),
          pendingPayRentSetupAfterTerms: true,
        });
        return;
      }
    } catch {
      // Continue to pay-rent setup when consent status cannot be verified.
    }
  }

  const tenantBuildingId = buildings?.[0]?.building.tenantBuildingId;
  if (!tenantBuildingId) {
    setModal('complete-building', { payRentSetupFlow: true });
    return;
  }
  void openPayRentFlowOrModals(tenantBuildingId, buildings, setModal, navigate, 'pay-rent');
}

export async function openPayRentFlowOrModals(
  tenantBuildingId: string,
  buildings: buildingDetails[] | undefined | null,
  setModal: (modal: string | null, state?: ModalState) => void,
  navigate: NavigateFunction,
  destination: 'pay-rent' | 'pay-now',
  payPlan: 'direct' | 'autopay' = 'direct'
): Promise<void> {
  // Check if the building exists and is active
  const selectedBuilding = buildings?.find(
    (b) => b.building.tenantBuildingId === tenantBuildingId
  );

  if (!selectedBuilding || !selectedBuilding.building.isActive) {
    setModal('complete-building', { payRentSetupFlow: true });
    return;
  }

  const prerequisitesMet = await ensurePayPrerequisitesMet(tenantBuildingId, buildings, setModal);
  if (!prerequisitesMet) return;

  if (destination === 'pay-now') {
    navigate('/pay-now', {
      state: {
        buildingId: tenantBuildingId,
        initialStep: 1,
        payPlan,
      },
    });
    return;
  }

  navigate('/pay-rent', { state: { buildingId: tenantBuildingId } });
}
